/**
 * Decision 0084 (build map `docs/design/project-pages/build-map.md` §1.5, §2.1, §3, §8.1): project and agent pages in
 * core. Pure: which document is which page, who may write a page, which pages a run pins, the Ready gate.
 *
 * Paths, kinds, caps and templates are protocol's (`@minga/protocol` `pages.ts`).
 */
import {
  agentPagePath,
  areaStatusPath,
  type DocMeta,
  docPathKey,
  isProposalPath,
  LOCKED_PAGE_KINDS,
  PAGE_CAPS,
  type PageKind,
  PROJECT_PAGES,
  pageKindOf,
  WORKSPACE_PREFIX,
} from "@minga/protocol"
import { documentByPath, documentHeads, notebookOf } from "./documents"
import { versionsOf } from "./outputs"
import { itemOf, leadOf } from "./state"
import { ancestorsOf } from "./tree"
import type { Actor, Agent, AgentId, Area, Artifact, GraphCommand, Item, ItemId, ProjectState } from "./types"
import { areaLeadOf } from "./workspace-pages"

type PageState = Pick<ProjectState, "artifacts" | "agents">

const PLANNING: ReadonlySet<string> = new Set(["triage", "backlog"])
const READY: ReadonlySet<string> = new Set(["todo", "in_progress"])

// #region pages
/**
 * The live page of `kind`: the project's overview or status, or `agentId`'s role, mandate, assumptions or notebook
 * (the notebook by `notebookFor` first, then by path). Guidelines are many: use {@link guidelinesFor}; `guideline`
 * returns undefined. An agent page without `agentId` (or with an unknown agent) is undefined.
 */
export const pageOf = (s: PageState, kind: PageKind, agentId?: AgentId): Artifact | undefined => {
  if (kind === "overview" || kind === "status" || kind === "decisions")
    return documentByPath(s, PROJECT_PAGES[kind])
  // decision 0086: an area's status page is per area (`areas/<slug>/status.md`), not per agent kind
  if (kind === "guideline" || kind === "area_status" || agentId === undefined) return undefined
  if (kind === "notebook") {
    const nb = notebookOf(s, agentId)
    if (nb) return nb
  }
  const agent = s.agents.find((a) => a.id === agentId)
  return agent ? documentByPath(s, agentPagePath(agent.handle, kind)) : undefined
}

const isPagesJob = (a: Actor): boolean => a._tag === "system" && a.process === "pages"

/** Decision 0086 (§1.2): the refusal for a project document under the reserved `workspace/` prefix. */
export const WORKSPACE_RESERVED_REFUSAL =
  "workspace/ is the workspace's knowledge: propose_page a workspace/ path"

/** Decision 0086 (§5): why agent `agentId` may not write the area status page at `path` (area `slug`), or null. */
const areaStatusRefusal = (s: ProjectState, agentId: AgentId, path: string, slug: string): string | null => {
  const lead = leadOf(s)
  if (lead?.id === agentId) return null // areas without a lead, and corrections
  const area = areaBySlug(s, slug)
  const owner = (area ? areaLeadOf(s, area.id) : undefined) ?? lead
  if (owner?.id === agentId) return null
  return `${path} is @${owner?.handle ?? "the area lead"}'s: propose your change to them`
}

const refusalAt = (s: ProjectState, actor: Actor, path: string, old: Artifact | undefined): string | null => {
  const page = pageKindOf(path)
  const proposal = isProposalPath(path)
  if (page === null && !proposal) return null
  switch (actor._tag) {
    case "user":
      return proposal ? `${path} is a propose_page draft: edit the page itself` : null
    case "agent": {
      if (proposal || page === null) return null
      if (LOCKED_PAGE_KINDS.has(page.kind))
        return `${path} is a person's page: propose your change with propose_page`
      if (page.kind === "area_status") return areaStatusRefusal(s, actor.agentId, path, page.area ?? "")
      if (page.kind === "notebook" || page.kind === "assumptions") {
        const own = s.agents.find((a) => a.id === actor.agentId)?.handle
        if (own === undefined || page.handle === null || docPathKey(own) !== docPathKey(page.handle))
          return `${path} belongs to @${page.handle ?? "another agent"}: write your own agents/<you>/ pages`
      }
      return null
    }
    case "worker":
      return `${path} is a project page: workers write no project pages`
    case "system": {
      if (!isPagesJob(actor) || proposal) return `${path}: only people and agents write this`
      if (old === undefined) return null // create (decide's path uniqueness refuses an existing page)
      // decision 0086 (§5): the job creates an area status page and never versions it
      if (page?.kind === "area_status") return `${path} exists: the pages job only creates pages`
      if (old.doc?.notebookFor !== undefined) return null // move a notebook to agents/<h>/notebook.md
      // versions of the chain the job is creating (brief history → overview v1..vn)
      if (versionsOf(s, old).every((v) => isPagesJob(v.createdBy))) return null
      return `${path} exists: the pages job only creates pages`
    }
    default:
      return `${path}: only people and agents write project pages`
  }
}

/**
 * §2.1: why `actor` may not post `next` (superseding `old`, if any), or null. Checked on the new path and on the
 * superseded version's path, so a lock can't be escaped by moving a page. Paths that are neither pages nor
 * `proposals/` drafts return null (the ordinary document rules apply). Decide wraps a refusal in Forbidden
 * `actor_kind`.
 *
 * Decision 0086: every actor's document at a new path under `workspace/` is refused (reserved for the workspace's
 * pages; `proposals/workspace/…` drafts pass; moving an older document out is allowed). `areas/<slug>/status.md`: people write it; of the agents, the area's lead
 * ({@link areaLeadOf}) and the project lead; workers never; the pages job creates it only.
 */
export const lockedPageRefusal = (
  s: ProjectState,
  actor: Actor,
  next: DocMeta,
  old: Artifact | undefined,
): string | null => {
  // decision 0086 (§1.2): `workspace/` addresses the workspace's pages; no project document may land there (the
  // propose_page draft path `proposals/workspace/…` does not match). Checked on the new path only, so a document
  // posted there before 0086 can still be moved out
  if (next.path !== null && docPathKey(next.path).startsWith(WORKSPACE_PREFIX))
    return WORKSPACE_RESERVED_REFUSAL
  const paths = [next.path, old?.doc?.path ?? null].filter((p): p is string => p !== null)
  for (const path of paths) {
    const r = refusalAt(s, actor, path, old)
    if (r !== null) return r
  }
  return null
}
// #endregion pages

// #region area status
/** The area with `slug` (case-insensitive, like `docPathKey`), active or archived. */
export const areaBySlug = (s: Pick<ProjectState, "areas">, slug: string): Area | undefined =>
  s.areas.find((a) => docPathKey(a.slug) === docPathKey(slug))

/** Decision 0086 (§5): the live `areas/<slug>/status.md` of `area`. */
export const areaStatusOf = (s: PageState, area: Area): Artifact | undefined =>
  documentByPath(s, areaStatusPath(area.slug))

/** One active area, its lead agent ({@link areaLeadOf}; undefined: the project lead covers it) and status page. */
export interface AreaStatus {
  readonly area: Area
  readonly owner: Agent | undefined
  readonly page: Artifact | undefined
}

/**
 * Decision 0086 (§5, §6.2): every active area in lane order with its lead and its status page. The Overview shows
 * those with a page or an owner; `ensure_pages` creates the page for those with an owner and no page.
 */
export const areaStatuses = (s: ProjectState): ReadonlyArray<AreaStatus> =>
  s.areas
    .filter((a) => a.status === "active")
    .sort((a, b) => (a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0))
    .map((area) => ({ area, owner: areaLeadOf(s, area.id), page: areaStatusOf(s, area) }))

/** Decision 0086 (§5): the active areas whose lead ({@link areaLeadOf}) is `agentId`, in lane order. */
export const areasLedBy = (s: ProjectState, agentId: AgentId): ReadonlyArray<Area> =>
  areaStatuses(s)
    .filter((a) => a.owner?.id === agentId)
    .map((a) => a.area)
// #endregion area status

// #region pinning
export interface PinnedPage {
  readonly artifact: Artifact
  readonly kind: PageKind
  readonly reason: "project_page" | "guideline"
  /** UTF-8 bytes; guidelines also share `GUIDELINES_TOTAL_CAP`. */
  readonly cap: number
  /** The header line: the page and its rule. */
  readonly label: string
}

/**
 * Decision 0088 addendum (stable/volatile split): the pages a turn's writes change most turns (status, the decision log,
 * the notebook, assumptions, area status). A turn pack keeps them out of AGENTS.md (a cloud mind's cached system
 * prompt) and puts them in the turn's prompt; the stable ones (overview, role, mandate) stay in AGENTS.md. Workers and
 * validators keep every pinned page in AGENTS.md (they have no history to cache).
 */
export const VOLATILE_PAGE_KINDS: ReadonlySet<PageKind> = new Set<PageKind>([
  "status",
  "decisions",
  "notebook",
  "assumptions",
  "area_status",
])

/** Decision 0088 addendum: whether a pinned page goes in the turn's prompt rather than AGENTS.md. */
export const isVolatilePage = (p: Pick<PinnedPage, "kind">): boolean => VOLATILE_PAGE_KINDS.has(p.kind)

const RULES: Readonly<Record<PageKind, string>> = {
  overview: "a person's page; propose changes with propose_page",
  status: "yours to keep current",
  decisions: "the decision log; append what was settled, newest last",
  role: "a person's page; propose changes with propose_page",
  mandate: "what you may decide; person-owned",
  assumptions: "your working assumptions; never authority",
  notebook: "your notebook",
  guideline: "how we work; follow it, and say so in your outcome if it is wrong",
  area_status: "yours to keep current: your area's where-we-are and next",
}

const pin = (
  artifact: Artifact | undefined,
  kind: PageKind,
  rule = RULES[kind],
): ReadonlyArray<PinnedPage> =>
  artifact
    ? [
        {
          artifact,
          kind,
          reason: kind === "guideline" ? "guideline" : "project_page",
          cap: PAGE_CAPS[kind],
          label: `${artifact.doc?.path ?? artifact.title} (${rule})`,
        },
      ]
    : []

/**
 * §3.2: the pages a run pins, in order, each with its cap. Turns (lead and role agents): overview, status, the
 * agent's own area status pages (an area lead, decision 0086 §5), the decision log (the lead only; 0084 addendum), own role, mandate, assumptions, notebook. Workers and validators: overview, status, a role agent's `role.md` as the
 * supervisor's guidance, then {@link guidelinesFor} the item. Only pages that exist are returned; the byte budget
 * (`PAGE_BUDGET`) is the pack builder's.
 */
export const pinnedPages = (
  s: ProjectState,
  run: {
    readonly kind: "turn" | "worker" | "validator"
    readonly agentId: AgentId
    readonly itemId: ItemId | null
    /** The item works in a repo (adds `guidelines/code.md`, §3.3). */
    readonly inRepo?: boolean
  },
): ReadonlyArray<PinnedPage> => {
  const project = [...pin(pageOf(s, "overview"), "overview"), ...pin(pageOf(s, "status"), "status")]
  if (run.kind === "turn")
    return [
      ...project,
      // decision 0086 (§5): an area lead's own area status, right after the project's
      ...areasLedBy(s, run.agentId).flatMap((a) => pin(areaStatusOf(s, a), "area_status")),
      ...(leadOf(s)?.id === run.agentId ? pin(pageOf(s, "decisions"), "decisions") : []),
      ...(["role", "mandate", "assumptions", "notebook"] as const).flatMap((k) =>
        pin(pageOf(s, k, run.agentId), k),
      ),
    ]
  const supervisor =
    leadOf(s)?.id === run.agentId
      ? []
      : pin(pageOf(s, "role", run.agentId), "role", "your supervisor's guidance")
  const item = run.itemId ? itemOf(s, run.itemId) : undefined
  const guidelines = item ? guidelinesFor(s, item, { inRepo: run.inRepo ?? false }) : []
  return [...project, ...supervisor, ...guidelines.flatMap((g) => pin(g, "guideline"))]
}

const areaStatusLabel = (a: AreaStatus, page: Artifact): string =>
  `${page.doc?.path ?? areaStatusPath(a.area.slug)} (${a.owner ? `@${a.owner.handle}'s area status` : `${a.area.name} status; the area has no lead`})`

/**
 * Decision 0084 addendum: the pages a run sees listed (ref and path, read on demand) but not pinned: the decision log
 * for everyone but the lead. Decision 0086 (§5): the project lead's turn also lists every area's status page (but one
 * it leads and pins); a worker or validator on an item in an area (`itemId`, the item or an ancestor) lists that
 * area's.
 */
export const listedPages = (
  s: ProjectState,
  run: {
    readonly kind: "turn" | "worker" | "validator"
    readonly agentId: AgentId
    /** The run's item (workers and validators): its area's status page is listed. */
    readonly itemId?: ItemId | null
  },
): ReadonlyArray<{ readonly artifact: Artifact; readonly label: string }> => {
  const isLead = leadOf(s)?.id === run.agentId
  const decisions = pageOf(s, "decisions")
  const listedDecisions =
    decisions && !(run.kind === "turn" && isLead)
      ? [
          {
            artifact: decisions,
            label: `${PROJECT_PAGES.decisions} (${RULES.decisions}; read it when a choice was made before)`,
          },
        ]
      : []
  const statuses = areaStatuses(s).filter(
    (a): a is AreaStatus & { readonly page: Artifact } => a.page !== undefined,
  )
  let areas: ReadonlyArray<AreaStatus & { readonly page: Artifact }> = []
  if (run.kind === "turn") {
    if (isLead) areas = statuses.filter((a) => a.owner?.id !== run.agentId)
  } else {
    const item = run.itemId ? itemOf(s, run.itemId) : undefined
    const areaId = item ? [item, ...ancestorsOf(s, item)].find((t) => t.areaId !== null)?.areaId : undefined
    if (areaId) areas = statuses.filter((a) => a.area.id === areaId)
  }
  return [...listedDecisions, ...areas.map((a) => ({ artifact: a.page, label: areaStatusLabel(a, a.page) }))]
}

const stemOf = (path: string): string => {
  const file = docPathKey(path).split("/").pop() ?? ""
  return file.replace(/\.md$/, "")
}

/**
 * §3.3: the guidelines a worker or validator on `item` pins, in order: `guidelines/README.md` or `general.md`; then,
 * by path, those whose file stem is the item's type, a tag of the item or an ancestor, or `code` when it works in a
 * repo; then those its or its ancestors' descriptions name by path or O-ref.
 */
export const guidelinesFor = (
  s: ProjectState,
  item: Item,
  opts: { readonly inRepo?: boolean } = {},
): ReadonlyArray<Artifact> => {
  const all = documentHeads(s).filter((a) => a.doc?.path && pageKindOf(a.doc.path)?.kind === "guideline")
  const chain = [item, ...ancestorsOf(s, item)]
  const keys = new Set<string>([
    item.type.toLowerCase(),
    ...chain.flatMap((t) => t.tags.map((x) => x.toLowerCase())),
  ])
  if (opts.inRepo) keys.add("code")
  const text = chain.map((t) => t.body).join("\n")
  const out: Artifact[] = []
  const add = (a: Artifact) => {
    if (!out.some((x) => x.id === a.id)) out.push(a)
  }
  for (const a of all) if (["readme", "general"].includes(stemOf(a.doc?.path ?? ""))) add(a)
  for (const a of all) if (keys.has(stemOf(a.doc?.path ?? ""))) add(a)
  for (const a of all) {
    const path = a.doc?.path ?? ""
    if (text.includes(path) || (a.ref && new RegExp(`\\b${a.ref}\\b`, "i").test(text))) add(a)
  }
  return out
}
// #endregion pinning

// #region planning
/** Is the item in Planning: itself or an ancestor in `triage` or `backlog` (§1.5)? */
export const isInPlanning = (s: ProjectState, item: Item): boolean =>
  PLANNING.has(item.status) || ancestorsOf(s, item).some((a) => PLANNING.has(a.status))

/**
 * §1.5, the Ready gate: the item a command makes Ready, or null. A MoveItem from triage/backlog to todo/in_progress;
 * a CreateItem whose status (default `todo`) is Ready when no ancestor is in Planning (Ready children under a
 * Planning parent are not gated: the parent's move is).
 *
 * Decided on the folded state: decide's policy check calls it on the changeset's scratch fold, so a parent created
 * earlier in the same changeset counts with the status it was created with (a plan created in Planning carries Ready
 * leaves ungated; a Ready parent gates its children too). A parent missing from `s` counts as not Planning.
 */
export const makesReady = (s: ProjectState, c: GraphCommand): ItemId | null => {
  if (c._tag === "MoveItem") {
    const t = itemOf(s, c.itemId)
    return t && c.status !== undefined && PLANNING.has(t.status) && READY.has(c.status) ? t.id : null
  }
  if (c._tag === "CreateItem") {
    if (!READY.has(c.status ?? "todo")) return null
    const parent = c.parentId ? itemOf(s, c.parentId) : undefined
    return parent && isInPlanning(s, parent) ? null : c.itemId
  }
  return null
}
// #endregion planning

/** The agent whose `agents/<h>/` folder a page path is in, by current handle (undefined: no such agent). */
export const pageAgentOf = (s: Pick<ProjectState, "agents">, path: string): AgentId | undefined => {
  const h = pageKindOf(path)?.handle
  return h ? s.agents.find((a) => docPathKey(a.handle) === docPathKey(h))?.id : undefined
}
